import axios from "axios";
import { initializeApp, getApps } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  deleteToken,
  isSupported,
} from "firebase/messaging";
import {
  getFirebaseClientConfig,
  getFcmVapidKey,
  isFcmClientEnabled,
  isFirebaseClientConfigComplete,
} from "../config/firebase";
import { canRegisterFcmForUser, resolveFcmApiPrefix } from "../helpers/fcm-role";
import { getServiceWorkerRegistration } from "../registerServiceWorker";

let messagingInstance = null;
let cachedDeviceToken = null;
let foregroundUnsubscribe = null;
let registrationInFlight = null;

function getMessagingInstance() {
  if (messagingInstance) {
    return messagingInstance;
  }
  if (!isFirebaseClientConfigComplete()) {
    return null;
  }
  const config = getFirebaseClientConfig();
  const app = getApps().length ? getApps()[0] : initializeApp(config);
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

export function getCachedFcmDeviceToken() {
  return cachedDeviceToken;
}

export async function isFcmSupportedInBrowser() {
  if (!isFcmClientEnabled()) {
    return false;
  }
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

export function getFcmNotificationPermission() {
  if (typeof Notification === "undefined") {
    return "denied";
  }
  return Notification.permission;
}

async function acquireAndRegisterFcmToken(authInfo, userInfo) {
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return { skipped: true, reason: "Service worker unavailable" };
  }

  const messaging = getMessagingInstance();
  if (!messaging) {
    return { skipped: true, reason: "Messaging unavailable" };
  }

  const deviceToken = await getToken(messaging, {
    vapidKey: getFcmVapidKey(),
    serviceWorkerRegistration: registration,
  });

  if (!deviceToken) {
    return { skipped: true, reason: "No FCM token returned" };
  }

  const apiPrefix = resolveFcmApiPrefix(userInfo);
  await registerTokenWithBackend(apiPrefix, authInfo.token, deviceToken);
  cachedDeviceToken = deviceToken;

  return { success: true, token: deviceToken };
}

/**
 * Register token when permission is already granted. Does NOT call requestPermission().
 */
export async function registerFcmTokenIfPermitted(authInfo, userInfo) {
  if (!isFcmClientEnabled() || !isFirebaseClientConfigComplete()) {
    return { skipped: true, reason: "FCM not configured" };
  }
  if (!(await isFcmSupportedInBrowser())) {
    return { skipped: true, reason: "FCM not supported" };
  }
  if (!authInfo?.token || !authInfo?.id || !canRegisterFcmForUser(userInfo)) {
    return { skipped: true, reason: "No eligible session" };
  }
  if (getFcmNotificationPermission() !== "granted") {
    return { skipped: true, reason: "Permission not granted" };
  }

  if (registrationInFlight) {
    return registrationInFlight;
  }

  registrationInFlight = (async () => {
    try {
      return await acquireAndRegisterFcmToken(authInfo, userInfo);
    } catch (error) {
      console.error("[FCM] registerFcmTokenIfPermitted error:", error.message);
      return { skipped: true, reason: error.message };
    } finally {
      registrationInFlight = null;
    }
  })();

  return registrationInFlight;
}

/**
 * User-gesture entry point: request permission, then register if granted.
 */
export async function enableFcmNotificationsFromUserGesture(authInfo, userInfo) {
  if (!isFcmClientEnabled() || !isFirebaseClientConfigComplete()) {
    return { skipped: true, reason: "FCM not configured" };
  }
  if (!(await isFcmSupportedInBrowser())) {
    return { skipped: true, reason: "FCM not supported" };
  }
  if (!authInfo?.token || !authInfo?.id || !canRegisterFcmForUser(userInfo)) {
    return { skipped: true, reason: "No eligible session" };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { skipped: true, reason: "Permission denied", permission };
    }
    return await registerFcmTokenIfPermitted(authInfo, userInfo);
  } catch (error) {
    console.error("[FCM] enableFcmNotificationsFromUserGesture error:", error.message);
    return { skipped: true, reason: error.message };
  }
}

/** @deprecated Use registerFcmTokenIfPermitted or enableFcmNotificationsFromUserGesture */
export async function registerFcmTokenForSession(authInfo, userInfo) {
  return registerFcmTokenIfPermitted(authInfo, userInfo);
}

async function registerTokenWithBackend(apiPrefix, authToken, deviceToken) {
  await axios.post(
    `${apiPrefix}/push-token/register`,
    {
      token: deviceToken,
      platform: "web",
      userAgent: navigator.userAgent || "",
    },
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  );
}

async function unregisterTokenWithBackend(apiPrefix, authToken, deviceToken) {
  if (!deviceToken) {
    return;
  }
  try {
    await axios.post(
      `${apiPrefix}/push-token/unregister`,
      { token: deviceToken },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
  } catch (error) {
    console.warn("[FCM] Backend unregister failed:", error.message);
  }
}

/**
 * Resolve the browser FCM token for logout/unregister. Uses in-memory cache when
 * available; after a full page refresh re-reads from Firebase via the same SW
 * registration and VAPID key (does not persist to localStorage).
 */
async function resolveCurrentFcmDeviceToken() {
  if (cachedDeviceToken) {
    return cachedDeviceToken;
  }
  if (!isFcmClientEnabled() || !isFirebaseClientConfigComplete()) {
    return null;
  }
  try {
    if (!(await isFcmSupportedInBrowser())) {
      return null;
    }
    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      return null;
    }
    const messaging = getMessagingInstance();
    if (!messaging) {
      return null;
    }
    const deviceToken = await getToken(messaging, {
      vapidKey: getFcmVapidKey(),
      serviceWorkerRegistration: registration,
    });
    return deviceToken || null;
  } catch (error) {
    console.warn("[FCM] resolveCurrentFcmDeviceToken failed:", error.message);
    return null;
  }
}

export async function unregisterFcmTokenForSession(authInfo, userInfo) {
  if (!isFcmClientEnabled()) {
    return;
  }

  const apiPrefix = resolveFcmApiPrefix(userInfo);
  const authToken = authInfo?.token;
  let deviceToken = null;

  try {
    deviceToken = await resolveCurrentFcmDeviceToken();
  } catch (error) {
    console.warn("[FCM] Could not resolve token for unregister:", error.message);
  }

  if (apiPrefix && authToken && deviceToken) {
    await unregisterTokenWithBackend(apiPrefix, authToken, deviceToken);
  }

  try {
    const messaging = getMessagingInstance();
    if (messaging && deviceToken) {
      await deleteToken(messaging);
    }
  } catch (error) {
    console.warn("[FCM] deleteToken failed:", error.message);
  }

  cachedDeviceToken = null;
}

export function subscribeFcmForeground(onPayload) {
  if (!isFcmClientEnabled() || !isFirebaseClientConfigComplete()) {
    return () => {};
  }

  const messaging = getMessagingInstance();
  if (!messaging) {
    return () => {};
  }

  if (foregroundUnsubscribe) {
    foregroundUnsubscribe();
  }

  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    onPayload(payload);
  });

  return () => {
    if (foregroundUnsubscribe) {
      foregroundUnsubscribe();
      foregroundUnsubscribe = null;
    }
  };
}

export async function refreshFcmTokenForSession(authInfo, userInfo) {
  if (getFcmNotificationPermission() !== "granted") {
    return { skipped: true, reason: "Permission not granted" };
  }
  return registerFcmTokenIfPermitted(authInfo, userInfo);
}
